import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { Model as DataModel, SortOptions } from '@/models/Model';
import SelectFilter from '@/components/select-filter/SelectFilter';
import debounce from 'debounce';
import { AxiosError } from 'axios';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { sortByKey } from '@/support/Object';
import { sanitizeString } from '@/support/String';
import { sanitize } from 'dompurify';
import {currency} from "@/support/String";
import ShoppingCart from "@/components/shopping-cart/ShoppingCart";

@Component<ItemTable>({
  components: {
    SelectFilter,
    LoaderCard
  },
  filters : {
    currency(value: string) {
      return !value ? '' : currency(parseFloat(value));
    },
  }
})
export default class ItemTable extends Vue {
  @Prop({ default: null })
  protected options!: TableOptions;

  @Prop({ default: 'id' })
  protected itemKey!: string;

  @Prop({ default: true })
  protected multiple!: boolean;

  @Prop({ default: false })
  protected compact!: boolean;

  @Prop({ default: false })
  protected editable!: boolean;
  protected initialized: boolean = false;

  @Watch("options")
  public optionsChanged(options: TableOptions, oldOptions: TableOptions) {
    if (this.initialized) {
      this.initialize();
    }
  }

  @Prop({ default: null })
  protected limit!: number;
  protected $limit: number = 10;
  protected limitSelected: number = 10;
  protected editMode: boolean = false;
  protected modal: boolean = false;

  @Prop({ default: 1 })
  protected page!: number;
  protected $page: number = 1;

  protected items: DataModel[] = [];
  protected changedItems: string[] = [];
  protected itemCache: any = {};
  protected total = 0;
  protected lastPage = 1;
  protected isLoading: boolean = true;
  protected showLoaderCard: boolean = false;

  protected TableHeader: object[] = [
    {
      text: "Name",
      value: "name"
    }
  ];

  protected pagination: object = {
    sortBy: "name",
    rowsPerPage: 5
  };

  protected name: TableName = {
    singular: "Item",
    plural: "Items"
  };

  protected $sort: undefined | SortOptions;

  @Prop({ default: () => {} })
  protected visibility!: TableVisibility;

  protected visible: TableVisibility = {
    search: true,
    total: true,
    limit: true,
    pagination: true,
    checkboxes: true,
    title: true,
    scrollable: false
  };

  @Prop({ default: false })
  protected clear!: boolean;

  @Prop({ default: () => [] })
  protected default!: string[];

  protected searchInput: string = '';

  protected tableActions: TableActions[] = [];
  protected tableFilters: TableFilters[] = [];

  public searchDebounce: Function = this.handleSearch();
  public paginationDebounce: Function = this.handlePagination();

  public selected: any = [];
  public selectedFilter: any = null;

  protected dateModals: object = {};

  public mounted() {
    if (this.default) {
      this.selected = this.default;
    } else {
      this.selected = [];
    }

    document.addEventListener("datatable:hook", (event: any) => {
      if (event.detail) {
        event.detail(this);
      }
    });

    this.initialize();
  }

  public initialize() {
    this.items = [];
    this.total = 0;
    this.isLoading = true;
    this.$page = this.page;
    this.$limit = this.limit;
    this.limitSelected = this.$limit;
    (this.pagination as any).rowsPerPage = this.$limit;
    this.visible = { ...{}, ...this.visible, ...this.visibility };

    this.TableHeader = [];
    this.tableActions = [];
    this.tableFilters = [];

    if (this.options.headers !== undefined) {
      this.TableHeader = this.options.headers;
    }

    if (this.options.actions !== undefined) {
      this.tableActions = this.options.actions;

      const hasActions =
        this.TableHeader.findIndex(
          (header: any) => header.value === "$actions"
        ) !== -1;
      if (this.tableActions.length > 0 && !hasActions) {
        this.TableHeader.push({ text: "", value: "$actions" });
      }
    }

    if (this.options.filters !== undefined) {
      this.tableFilters = this.options.filters;
    }

    if (
      this.options.sort !== undefined &&
      this.options.sort.key !== undefined
    ) {
      this.$sort = this.options.sort as SortOptions;
    }

    if (this.options.name !== undefined) {
      this.name = { ...this.name, ...this.options.name };
    }
    this.fetchSource();
  }

  public fetchSource(dispatchToStore?: boolean) {
    this.isLoading = true;

    if (this.$sort && this.$sort.key) {
      this.options.model.sort(this.$sort.key, this.$sort.order);
    }

    this.options.model
      .limit(this.$limit)
      .page(this.$page)
      .filter("search", this.searchInput)
      .all()
      .then((items: DataModel[]) => {
        this.items = items;
        this.$emit("dataLoaded", this.items);
        this.total = items[0] !== undefined ? (items[0].meta as TableMeta).total : 0;
        this.lastPage = items[0] !== undefined ? (items[0].meta as TableMeta).last_page : 1;
        this.createDateModals();

        if (dispatchToStore) {
          let payload: { [key: string]: DataModel[] } = {};
          payload[`${this.options.model.getInstanceName()}`] = items;
          this.$store.dispatch("updateFilters", payload);
        }

        this.initialized = true;
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        this.isLoading = false;
      });
  }

  public createDateModals() {
    const fields: string[] = [];
    this.options.headers.forEach((header: TableHeader, index: number) => {
      if ((header as any).type && (header as any).type === "date") {
        fields.push(header.value);
      }
    });

    this.items.forEach((item: DataModel) => {
      fields.forEach((field: string) => {
        const key = `${(item as any).id}_${field}`;
        this.$set(this.dateModals, key, false);
      });
    });
  }

  public select(items: any[]) {
    this.selected = [...items, ...this.selected];
  }

  public unselect(items: any[]) {
    this.selected = this.selected.filter((item: any) => {
      return !items.includes(item);
    });
  }

  public refresh(dispatchToStore?: boolean) {
    this.fetchSource(dispatchToStore);
  }

  public deleteItem(item: DataModel) {
    this.isLoading = true;
    item.delete().then((resonse: any) => {
      this.fetchSource();
    });
  }

  public toggleAll() {
    if (this.selected.length) {
      this.selected = [];
    } else {
      this.selected = this.items.slice();
    }
  }

  public handlePagination() {
    return debounce(
      (page: number) => {
        this.$page = page;
        this.fetchSource();
      },
      300,
      true
    );
  }

  public handleAction(action: Function, item: DataModel) {
    action(this, item);
    return;
  }

  public handleIcon(action: Function, item: DataModel) {
    return action(this, item);
  }

  public setItemChanged(item: DataModel) {
    if (!this.changedItems.includes((item as any).id)) {
      this.changedItems.push((item as any).id);
    }
  }

  public resetDateField(item: DataModel, property: string) {
    if (!this.itemCache[(item as any).id]) {
      return;
    }
    (item as any)[property] = this.itemCache[(item as any).id][property];
    (this.dateModals as any)[`${(item as any).id}_${property}`] = false;
  }

  public cancelEdit() {
    Object.keys(this.itemCache).forEach((key: string) => {
      const item: any = this.items.find((level: any) => {
        return level.id === key;
      });

      if (!item) {
        return;
      }

      Object.keys(this.itemCache[key]).forEach((property: string) => {
        item[property] = this.itemCache[key][property];
      });
    });

    this.clearChangedItems();
    this.editMode = false;
  }

  public handleTableEdit() {}

  public minDate(header: any) {
    if (!header.min) {
      return "";
    }
    return header.min;
  }

  public maxDate(header: any) {
    if (!header.max) {
      return "";
    }
    return header.max;
  }

  public handleSearch() {
    return debounce((searchInput: string) => {
      this.$page = 1;
      this.fetchSource();
    }, 400);
  }

  /**
   * datatable column headers can be printed out simply (header.breakTitle = false)
   * they can be split in lines by their \s (header.breakTitle = true & is NOT array header.breakTitleArray)
   * or they can be split in lines by defined strings (header.breakTitleArray =['sluitings','datum'])
   *
   * @param {*} header
   * @returns
   * @memberof DataTable
   */
  public createTitle(header: any) {
    if (!header.breakTitle) return header.text;

    const start = "<span class='column-header-flex'>";
    const end = "</span>";
    if (Array.isArray(header.breakTitleArray)) {
      return (
        start +
        header.breakTitleArray.map(this.wrapWithColumnHeaderRow).join(``) +
        end
      );
    } else {
      return (
        start +
        header.text
          .split(" ")
          .map(this.wrapWithColumnHeaderRow)
          .join(``) +
        end
      );
    }
  }

  private wrapWithColumnHeaderRow(text: string) {
    return `<span class='column-header-flex-row'>${text}</span>`;
  }

  public createItemCache() {
    this.clearItemCache();
    this.items.forEach((item: any) => {
      const itemProperties: any = {};
      this.TableHeader.forEach((header: any) => {
        if (header.value !== "$actions" && header.editable) {
          itemProperties[header.value] = item[header.value];
        }
      });
      this.itemCache[item.id] = itemProperties;
    });
  }

  public update() {
    let callCounter = 0;
    const changed = this.items.filter((item: DataModel) => {
      return this.changedItems.includes((item as any).id);
    });

    this.showLoaderCard = true;
    changed.forEach((item: DataModel) => {
      item
        .update()
        .then((model: DataModel) => {
          item = model;
          callCounter += 1;

          if (callCounter >= changed.length) {
            this.showLoaderCard = false;
            this.clearChangedItems();
            this.editMode = false;
            this.$emit("data-update");
            this.refresh();
          }
        })
        .catch((error: AxiosError) => {
          this.showLoaderCard = false;
        });
    });
  }

  public visibleForRole(roles: string[]) {
    if (!roles) {
      return true;
    }

    return this.$store.state.Auth.hasRole(roles);
  }

  public clearChangedItems() {
    this.changedItems = [];
  }

  protected isActionVisible(visible: any, item: DataModel) {
    if (visible === undefined) {
      return true;
    }

    if (this.isFunction(visible)) {
      return visible(this, item);
    }

    return visible;
  }

  public clearItemCache() {
    Object.keys(this.itemCache).forEach((key: string) => {
      delete this.itemCache[key];
    });
  }

  public isFunction(object: any) {
    return object && {}.toString.call(object) == "[object Function]";
  }

  public changeSorting(sortable: SortOptions) {
    if (!sortable) {
      return;
    }

    if (sortable.order === "ASC") {
      sortable.order = "DESC";
    } else {
      sortable.order = "ASC";
    }

    this.$sort = sortable;

    this.fetchSource();
  }

  public isSortable(header: any) {
    if (!header.sortable) {
      return false;
    }
    return true;
  }

  public getExtraClass(object: any) {
    let classString: string = "";
    // header breaking title
    if ("breakTitle" in object) {
      classString += "broken-title";
    }
    if (object.class) {
      if (this.isFunction(object)) {
        return object.class() + " " + classString;
      } else {
        return object.class + " " + classString;
      }
    } else {
      return classString;
    }
  }

  protected santizeString(value: string) {
    if (!value) {
      return "";
    }

    return sanitizeString(value, true);
  }

  @Watch("selected")
  public selectionChange(value: any, oldValue: any) {
    if (!this.multiple && this.selected.length > 1) {
      this.selected = [this.selected[this.selected.length - 1]];
    }
    this.$emit("input", this.selected);
  }

  // @Watch('default')
  // protected defaultChanged(value: any, oldValue: any) {
  //   if (value !== null && value !== undefined && value.length > 0 && this.selected !== value) {
  //     this.selected = value;
  //   }
  // }

  @Watch("selectedFilter")
  public selectedFilterChange(value: any, oldValue: string) {
    this.options.model.filter(value.type.toLowerCase(), value.value);
    this.fetchSource();
  }

  @Watch("limitSelected")
  public limitChanged(limit: number, oldLimit: number) {
    if (limit === null) {
      return;
    }

    this.$limit = limit;
    this.$page = 1;
    this.fetchSource();
  }

  @Watch("editMode")
  public editModeToggled(isEditting: boolean) {
    if (isEditting) {
      this.createItemCache();
    }
  }

  @Watch("clear")
  protected clearSelect(clear: boolean) {
    if (clear) {
      this.selected = [];
      this.searchInput = "";
      this.fetchSource();
      this.$emit("cleared");
    }
  }

  public clearSelectBoxes() {
    this.selected = [];
    this.searchInput = "";
    this.fetchSource();
    this.$emit("cleared");
  }

  public headerClasses(header: any) {
    const defaultClasses = {
      "icon-column": header.value === "icon",
      "color-column": header.value === "color",
      "avatar-column": header.value === "avatar",
      "actions-column": header.value === "$actions",
      "sortable-column": this.isSortable(header),
      asc: this.isSortable(header) && header.sortable.order === "ASC",
      desc: this.isSortable(header) && header.sortable.order === "DESC"
    };

    const customClasses = header.class ? header.class : {};

    return { ...defaultClasses, ...customClasses };
  }

  public columnStyles(header: any) {
    return header.style ? header.style : {};
  }

  protected sanitizeString(value: string) {
    if (!value) {
      return "";
    }

    return sanitizeString(value, true);
  }

  protected get hasOptionsSlot() {
    return !!this.$slots.options;
  }
}

export interface TableMeta {
  total: number;
  last_page: number;
}

export interface TableOptions {
  model: DataModel;
  name?: TableName;
  headers: TableHeader[];
  actions: TableActions[];
  filters: TableFilters[];
  sort?: TableSort;
  add?: Function;
}

export interface TableHeader {
  text: string;
  value: any;
  action?: string | Function;
  sortable?: TableSort;
  class?: string;
  width?: string;
  transform?: Function;
}

export interface TableFilters {
  type: string;
  text: string;
  value: any;
  multiple?: boolean;
}

export interface TableName {
  singular: string;
  plural: string;
}

export interface TableActions {
  type: string;
  label: any;
  icon: string | Function;
  action: string | Function;
  roles?: string[];
  tooltip?: string;
}

export interface TableVisibility {
  title?: boolean;
  search?: boolean;
  total?: boolean;
  limit?: boolean;
  pagination?: boolean;
  checkboxes?: boolean;
  scrollable?: boolean;
}

export interface TableSort {
  key: string;
  order: string;
}
